import CornerBg from "@/assets/img/illustrations/corner-2.png";
import Background from "@/components/atoms/Background";
import EmailLink from "@/components/atoms/EmailLink";
import ErrorComponent from "@/components/atoms/ErrorComponent";
import Flex from "@/components/atoms/Flex";
import IconButton from "@/components/atoms/IconButton";
import Loader from "@/components/atoms/Loader";
import PhoneCallLink from "@/components/atoms/PhoneCallLink";
import SoftBadge from "@/components/atoms/SoftBadge";
import UnknownBadge from "@/components/atoms/UnknownBadge";
import {
  getColorFromRole,
  getReelRoleName,
  getReelStateColor,
  getReelStateName,
} from "@/helpers/UserHelpers";
import { useClickToCall } from "@/hooks/useClickToCall";
import { CardDetailsProps } from "@/interfaces/Card";
import IUser from "@/interfaces/IUser";
import { useGetUserQuery } from "@/services/users";
import { faEye, faPhone, faPlus } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { ElementRef, FC, useEffect, useRef } from "react";
import { Card, Col, Form, Image, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import CardHeader from "../../card-headers/CardHeader";
import StoreUserNoteModal from "../../modals/users/StoreUserNoteModal";

const UserDetailsCard: FC<CardDetailsProps<IUser>> = ({
  id,
  showDetailsButton,
  callBack,
}) => {
  const StoreUserNoteModalRef =
    useRef<ElementRef<typeof StoreUserNoteModal>>(null);

  const { data, isLoading, isError, error } = useGetUserQuery(id);
  const onCall = useClickToCall();

  useEffect(() => {
    data && callBack?.(data);
  }, [data]);

  const onTriggerModal = () =>
    StoreUserNoteModalRef.current?.showOrHiddenModal();

  if (isLoading) {
    return <Loader absolute />;
  } else if (isError) {
    return <ErrorComponent error={error} absolute returnButton />;
  }

  return (
    <Card>
      <CardHeader title="Information de l'utilisateur" />
      <Card.Body className="bg-light">
        <StoreUserNoteModal ref={StoreUserNoteModalRef} id={id} />
        <Card className="border rounded-3 bg-white dark__bg-1000 shadow-none">
          <Background
            image={CornerBg}
            className="bg-card d-none d-sm-block d-xl-none"
          />
          <Card.Body className="row g-0 flex-column flex-sm-row flex-xl-column z-index-1 align-items-center">
            <Col sm={8} md={6} lg={4} xl={12} className="ps-sm-1 ps-xl-0">
              <Flex alignItems="xl-center" direction="column">
                {data?.avatar?.link && (
                  <Image
                    fluid
                    className="rounded-3 mb-2"
                    width={112}
                    src={data.avatar.link}
                    alt="student profile image"
                  />
                )}
                <p className="fw-semi-bold text-800 mb-0">{data!.fullname}</p>
                <Flex>
                  {data?.roles.map((role, index) => (
                    <SoftBadge
                      key={role}
                      bg={getColorFromRole([role])}
                      className={classNames("my-2 text-xl-center", {
                        "ms-2": index !== 0,
                      })}
                    >
                      {getReelRoleName([role])}
                    </SoftBadge>
                  ))}
                  <SoftBadge
                    bg={getReelStateColor(data!.isActive)}
                    className="my-2 ms-2 text-xl-center"
                  >
                    {getReelStateName(data!.isActive)}
                  </SoftBadge>
                </Flex>
              </Flex>
              <Flex justifyContent="center" className="gap-2">
                {showDetailsButton ? (
                  <IconButton
                    variant="primary"
                    icon={faEye}
                    iconAlign="left"
                    href={`/companies/${data!.id}`}
                    transform="shrink-3 down-1"
                    size="sm"
                    className="w-50 px-2 text-nowrap"
                  >
                    <span className="fs--2">Détails</span>
                  </IconButton>
                ) : (
                  <IconButton
                    variant="primary"
                    icon={faPlus}
                    iconAlign="left"
                    onClick={onTriggerModal}
                    transform="shrink-3 down-1"
                    size="sm"
                    className="w-50 px-2 text-nowrap"
                  >
                    <span className="fs--2">Note</span>
                  </IconButton>
                )}
                {data?.phone && (
                  <IconButton
                    variant="nolina-default"
                    icon={faPhone}
                    iconAlign="left"
                    transform="shrink-4"
                    size="sm"
                    onClick={() => onCall(data.phone!)}
                    className="w-50"
                  >
                    <span className="fs--2">{data?.phone}</span>
                  </IconButton>
                )}
              </Flex>
            </Col>
          </Card.Body>
        </Card>
        <Row className="border rounded-3 p-x1 mt-3 bg-white dark__bg-1000 mx-0 g-0">
          <Col md={6} xl={12} className="pe-md-4 pe-xl-0">
            <div className="mb-2">
              <Form.Label>#</Form.Label>
              <p className="mb-0 text-700 fs--1">{data!.id}</p>
            </div>
            <div className="mb-2">
              <Form.Label>Prénom</Form.Label>
              <p className="mb-0 text-700 fs--1">{data!.firstname}</p>
            </div>
            <div className="mb-2">
              <Form.Label>Nom</Form.Label>
              <p className="mb-0 text-700 fs--1">{data!.lastname}</p>
            </div>
            <div className="mb-2">
              <Form.Label>Email</Form.Label>
              <br />
              <EmailLink email={data!.email} />
            </div>
            <div className="mb-2">
              <Form.Label>Numéro de téléphone</Form.Label>
              <br />
              {data?.phone ? (
                <PhoneCallLink number={data.phone} />
              ) : (
                <UnknownBadge />
              )}
            </div>
            {data?.signatureFile?.link && (
              <div className="mb-2">
                <Form.Label>Signature électronique</Form.Label>
                <br />
                <Image
                  fluid
                  className="rounded-3 mt-2"
                  style={{ maxWidth: "200px", maxHeight: "100px", objectFit: "contain" }}
                  src={data.signatureFile.link}
                  alt="signature électronique"
                />
              </div>
            )}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default UserDetailsCard;
